import { type ReactNode, type RefObject, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useAnchoredPosition } from '@shared/lib/anchor-position';
import { cn } from '@shared/lib/cn';
import { usePortalContainer } from '@shared/lib/portal';

import { POPOVER_OFFSET_PX } from './popover.constant';

export interface AnchoredLayerProps {
  /** 붙일 기준 요소. 상자는 그 아래 왼쪽 맞춤으로 뜬다 */
  anchorRef: RefObject<HTMLElement | null>;
  /** 떠 있는 틀의 ref — 바깥을 눌렀는지 가릴 때 쓴다 */
  layerRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  children: ReactNode;
}

/**
 * 기준 요소 아래에(화면 아래가 모자라면 위에) 상자(DatePicker 달력 · TimePicker 열 등)를 portal + fixed 로 띄우는 틀.
 * 스크롤 · 크기 변경을 따라가고, 겹쳐 뜨는 창 안이면 그 창의 덮개 틀에 붙는다(`usePortalContainer`) —
 * 팝업 본문의 스크롤 영역에 잘리지 않는다.
 */
export function AnchoredLayer({ anchorRef, layerRef, className, children }: AnchoredLayerProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const ref = layerRef ?? innerRef;
  const container = usePortalContainer();

  // 아래가 모자라면 위로 뒤집는다(달력은 줄이지 않는다)
  useAnchoredPosition(anchorRef, ref, { gap: POPOVER_OFFSET_PX });

  return createPortal(
    <div
      ref={ref}
      data-slot="anchored-layer"
      className={cn('invisible fixed top-0 left-0 z-(--z-dropdown)', className)}
    >
      {children}
    </div>,
    container,
  );
}
